import React, { useCallback, type ReactElement } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { ToolTipContent, toggleMarkdownView } from '@atlaskit/editor-common/keymaps';
import { markdownModeMessages as i18n } from '@atlaskit/editor-common/messages';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ToolbarSection, ToolbarButton, ToolbarTooltip } from '@atlaskit/editor-toolbar';
import TextNormalIcon from '@atlaskit/icon-lab/core/text-normal';
import ViewSplitIcon from '@atlaskit/icon-lab/core/view-split';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { MarkdownModePlugin, MarkdownModeView } from '../markdownModePluginType';

const styles = cssMap({
	wrapper: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'button[aria-pressed="true"]': {
			borderColor: token('color.border.selected'),
			borderWidth: token('border.width'),
			borderStyle: 'solid',
			width: '28px',
		},
	},
});

// ---------------------------------------------------------------------------
// Base (presentational) component — shared by both the plugin toolbar toggle
// and the Confluence floating toggle.
// ---------------------------------------------------------------------------

export type MarkdownModeViewToggleBaseProps = {
	/** Called when the user clicks a view button. */
	onViewChange: (nextView: MarkdownModeView) => void;
	/** Current active view. */
	view: MarkdownModeView;
};

/**
 * Pure presentational 3-button view-mode toggle (syntax / preview / split-view).
 *
 * This component is stateless — the consumer is responsible for providing the
 * current `view` and handling `onViewChange`. This allows it to be shared
 * between the editor plugin (ProseMirror state) and Confluence (sweet-state).
 */
export const MarkdownModeViewToggleBase = ({
	view,
	onViewChange,
}: MarkdownModeViewToggleBaseProps): ReactElement => {
	const intl = useIntl();

	const handleClick = useCallback(
		(nextView: MarkdownModeView) => {
			if (nextView === view) {
				return;
			}
			onViewChange(nextView);
		},
		[view, onViewChange],
	);

	// Stable per-view click handlers so the IconButtons receive a referentially
	// equal `onClick` prop across renders (avoids breaking memoisation in the
	// IconButton subtree). `handleClick` itself is already memoised on
	// (view, onViewChange).
	const handleSyntaxClick = useCallback(() => handleClick('syntax'), [handleClick]);
	const handlePreviewClick = useCallback(() => handleClick('preview'), [handleClick]);
	const handleSplitViewClick = useCallback(() => handleClick('split-view'), [handleClick]);

	const syntaxLabel = intl.formatMessage(i18n.source);
	const previewLabel = intl.formatMessage(i18n.wysiwyg);
	// Note: i18n message keys (`i18n.source`, `i18n.sourceAriaLabel`) are
	// intentionally left as-is to preserve translation memory for the
	// 'Syntax editor' / 'Markdown source' strings already in production.
	const splitViewLabel = intl.formatMessage(i18n.preview);
	const groupLabel = intl.formatMessage(i18n.groupLabel);

	return (
		<Box
			xcss={styles.wrapper}
			role="group"
			aria-label={groupLabel}
			testId="markdown-mode-view-toggle"
		>
			<ToolbarSection>
				<ToolbarTooltip
					content={
						<ToolTipContent
							description={syntaxLabel}
							keymap={view === 'preview' ? toggleMarkdownView : undefined}
						/>
					}
				>
					<ToolbarButton
						iconBefore={<AngleBracketsIcon size="small" label={syntaxLabel} />}
						label={syntaxLabel}
						isSelected={view === 'syntax'}
						onClick={handleSyntaxClick}
						testId="markdown-mode-view-toggle--syntax"
						ariaKeyshortcuts={view === 'preview' ? toggleMarkdownView.description : undefined}
					/>
				</ToolbarTooltip>
				<ToolbarTooltip
					content={
						<ToolTipContent
							description={previewLabel}
							keymap={view === 'syntax' ? toggleMarkdownView : undefined}
						/>
					}
				>
					<ToolbarButton
						iconBefore={<TextNormalIcon size="small" label={previewLabel} />}
						label={previewLabel}
						isSelected={view === 'preview'}
						onClick={handlePreviewClick}
						testId="markdown-mode-view-toggle--preview"
						ariaKeyshortcuts={view === 'syntax' ? toggleMarkdownView.description : undefined}
					/>
				</ToolbarTooltip>
				<ToolbarTooltip content={<ToolTipContent description={splitViewLabel} />}>
					<ToolbarButton
						iconBefore={<ViewSplitIcon size="small" label={splitViewLabel} />}
						label={splitViewLabel}
						isSelected={view === 'split-view'}
						onClick={handleSplitViewClick}
						testId="markdown-mode-view-toggle--split-view"
						isDisabled
					/>
				</ToolbarTooltip>
			</ToolbarSection>
		</Box>
	);
};

// ---------------------------------------------------------------------------
// Plugin-connected wrapper — reads and writes ProseMirror plugin state.
// ---------------------------------------------------------------------------

export type MarkdownModeViewToggleProps = {
	api: ExtractInjectionAPI<MarkdownModePlugin> | undefined;
};

export const MarkdownModeViewToggle = ({
	api,
}: MarkdownModeViewToggleProps): ReactElement | null => {
	const { view, isMarkdownMode } = useSharedPluginStateWithSelector(
		api,
		['markdownMode'],
		(states) => ({
			view: states.markdownModeState?.view ?? ('preview' as const),
			isMarkdownMode: states.markdownModeState?.isMarkdownMode ?? false,
		}),
	);

	const handleViewChange = useCallback(
		(nextView: MarkdownModeView) => {
			api?.markdownMode?.actions.setView(nextView);
		},
		[api],
	);

	if (!isMarkdownMode) {
		return null;
	}

	return <MarkdownModeViewToggleBase view={view} onViewChange={handleViewChange} />;
};
